/**
 * Der Kopf des Berichts und „So sind wir vorgegangen, und was jeder Schritt
 * ergab“ (seit 12.10.2026, Entwurf „Final · C mit Überschrift aus B“ im Canvas
 * „99takes Bericht verständlich“, von Robert gewählt).
 *
 * ANLASS: ein Leser fand den Magnolia-Bericht schwer verständlich. Die
 * Überschrift war ein Ergebnis in Fachsprache, oben standen Zahlen ohne
 * Herleitung, und unklar blieb, ob die bestehende Seite oder eine Alternative
 * getestet wurde. Jetzt sagt die Überschrift, WAS getestet wurde und bei WEM;
 * drei Kästen sagen, was gegen was antrat, wer urteilte und was herauskam; die
 * Zeitleiste führt Schritt für Schritt zum Ergebnis.
 *
 * Nur Rechnung, keine Darstellung (die steht in `seiten/Bericht.tsx`). Alles
 * aus der eingefrorenen Nutzlast. Die neuen Felder des Autors (`narrative.titel`,
 * `getestet`, `ausgangslage`, `frage`, `fassungen`) fehlen in älteren
 * Berichten; dann rechnet jede Funktion einen Ersatz oder lässt den Teil weg.
 */

import { anzahlUrteile, personaNoten } from './bericht-noten';
import { leseKernRand, randWarnung } from './kern-rand';
import { fassungsname } from './fassungsname';
import { designEintraege, type FassungRef } from './bericht-ablauf';
import { bilderVon, designKarte, ersteZeile, kernRandZahl, textStufe, weiterSatz, designStufen, type FassungsStufe, type GeraetStufe } from './bericht-blatt';
import { vergleichFuerSatz, vergleichIstHeute } from './bericht-vorschlag';
import { bleibenText } from './erster-bildschirm';
import { locale, sprache, t } from './sprache';

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const en = () => sprache() === 'en';
const zahlWort = (n: number) => (en()
  ? ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve']
  : ['null', 'eine', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf'])[n] ?? String(n);
const gross = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const varianten = (data: any): any[] => (Array.isArray(data?.variants) ? data.variants : []);
const personasVon = (data: any): any[] => (Array.isArray(data?.set?.personas) ? data.set.personas : []);

// ─── Kopf ────────────────────────────────────────────────────────────────────

/** Die H1: was getestet wurde und bei wem (`narrative.titel`), sonst der Name der Studie. Nie das Urteil. */
export function titelDesBerichts(data: any): string {
  return text(data?.narrative?.titel) || text(data?.study?.name) || t('Bericht', 'Report');
}

/**
 * Wogegen die Fassungen antraten, im Nominativ für den Satzanfang: der Name des
 * Autors (`narrative.vergleichsfassung`), „die heutige Seite“, „eine von 99takes
 * erzeugte Vergleichsfassung“ oder „eure Vergleichsfassung“.
 */
export function vergleichImSatz(data: any): string {
  const vs = varianten(data).filter((v) => v?.mode !== 'feedback');
  const vf = text(data?.narrative?.vergleichsfassung);
  if (vf) return vf;
  if (vs.length && vs.every((v) => vergleichIstHeute(data, v))) return t('die heutige Seite', 'the current page');
  return vs.length && vs.every((v) => v?.baseline_source === 'generated')
    ? t('eine von 99takes erzeugte Vergleichsfassung', 'a baseline generated by 99takes') : t('eure Vergleichsfassung', 'your baseline');
}

/**
 * Kasten „Getestet“: `narrative.getestet`, sonst gerechnet: „Drei Fassungen,
 * jede einzeln gegen eure Vergleichsfassung.“ Seit 13.10.2026 mit „jede
 * einzeln“: „Eure Vergleichsfassung gegen drei Fassungen“ las ein Leser als
 * vier Texte gleichzeitig gegeneinander. Ohne Vergleich (nur Feedback-Runs):
 * wie viele Fassungen einzeln gelesen wurden.
 */
export function getestetSatz(data: any): string {
  const eigen = text(data?.narrative?.getestet);
  if (eigen) return eigen;
  const vs = varianten(data);
  if (!vs.length) return '';
  const vergleiche = vs.filter((v) => v?.mode !== 'feedback').length;
  if (en()) {
    if (!vergleiche) return vs.length === 1 ? 'One version, read on its own, without a baseline.' : `${gross(zahlWort(vs.length))} versions, each read on its own, without a baseline.`;
    return vergleiche === 1
      ? `${gross(vergleichImSatz(data))} against one new version.`
      : `${gross(zahlWort(vergleiche))} versions, each one separately against ${vergleichImSatz(data)}.`;
  }
  if (!vergleiche) return vs.length === 1 ? 'Eine Fassung, einzeln gelesen, ohne Vergleichsfassung.' : `${gross(zahlWort(vs.length))} Fassungen, je einzeln gelesen, ohne Vergleichsfassung.`;
  return vergleiche === 1
    ? `${gross(vergleichImSatz(data))} gegen eine neue Fassung.`
    : `${gross(zahlWort(vergleiche))} Fassungen, jede einzeln gegen ${vergleichImSatz(data)}.`;
}

/**
 * Kasten „Geurteilt haben“: „13 Personas aus der Zielgruppe. Gezählt werden die
 * 8 aus der Kernzielgruppe.“ Ohne Kern und Rand: „13 Personas aus der
 * Zielgruppe „Name“.“ Ohne Personas im Bericht leer.
 */
export function geurteiltSatz(data: any): string {
  const n = personasVon(data).length;
  if (!n) return '';
  const kr = kernRandZahl(data);
  const name = text(data?.set?.name);
  if (en()) {
    if (kr) return `${n} personas from the audience. Only the ${kr.kern} from the core audience count.`;
    return `${n} ${n === 1 ? 'persona' : 'personas'} from the audience${name ? ` “${name}”` : ''}.`;
  }
  if (kr) return `${n} Personas aus der Zielgruppe. Gezählt werden die ${kr.kern} aus der Kernzielgruppe.`;
  return `${n} ${n === 1 ? 'Persona' : 'Personas'} aus der Zielgruppe${name ? ` „${name}“` : ''}.`;
}

// ─── Schritt 1: was vorlag ───────────────────────────────────────────────────

/** „rund 12.000 Zeichen“: ab 2.000 auf Tausend, ab 200 auf Hundert, darunter auf Zehn. */
export function rundZeichen(wert: unknown): string {
  const n = typeof wert === 'string' ? wert.trim().length : 0;
  if (!n) return '';
  const stelle = n >= 2000 ? 1000 : n >= 200 ? 100 : 10;
  const r = Math.max(stelle, Math.round(n / stelle) * stelle);
  return t(`rund ${new Intl.NumberFormat(locale()).format(r)} Zeichen`, `about ${new Intl.NumberFormat(locale()).format(r)} characters`);
}

export interface Ausgangslage {
  titel: string;
  /** Sätze für Schritt 1. Leer, wenn es nichts zu sagen gibt. */
  saetze: string[];
}

/**
 * Schritt 1. Text aus `narrative.ausgangslage`; sonst das Angebot der Studie,
 * gekürzt, und die Länge der Vergleichsfassung. Titel „Entwürfe gelesen“, bei
 * der heutigen Seite als Vergleich „Die heutige Seite gelesen“.
 */
export function ausgangslage(data: any): Ausgangslage {
  const stand = varianten(data).at(-1);
  const heute = !!stand && vergleichIstHeute(data, stand);
  const titel = heute ? t('Die heutige Seite gelesen', 'Current page read') : t('Entwürfe gelesen', 'Drafts read');
  const eigen = text(data?.narrative?.ausgangslage);
  if (eigen) return { titel, saetze: [eigen] };
  const saetze: string[] = [];
  const angebot = ersteZeile(data?.study?.offer, 220);
  if (angebot) saetze.push(t(`Angebot und Ziel: ${angebot}`, `Offer and goal: ${angebot}`));
  const laenge = rundZeichen(varianten(data).find((v) => text(v?.baseline_text))?.baseline_text);
  if (laenge) saetze.push(t(`${gross(vergleichImSatz(data))} hat ${laenge}.`, `${gross(vergleichImSatz(data))} has ${laenge}.`));
  return { titel, saetze };
}

// ─── Schritt 2: die Zielgruppe ───────────────────────────────────────────────

export interface ZielgruppenSchritt {
  personas: number;
  belege: number;
  kern: number | null;
  /** Personas der erweiterten Zielgruppe und ihre Rollen, kurz. */
  erweitert: { n: number; rollen: string[] } | null;
}

/** Die Rolle kurz: „Datenschutzbeauftragte, AI Governance“ wird „Datenschutzbeauftragte“. */
const kurzeRolle = (p: any): string => {
  const r = text(p?.role) || text(p?.name);
  const vorn = r.split(/[,;(]/)[0]!.trim();
  return vorn.length > 40 ? `${vorn.slice(0, 40).replace(/\s+\S*$/, '')} …` : vorn;
};

export function zielgruppe(data: any): ZielgruppenSchritt | null {
  const ps = personasVon(data);
  if (!ps.length) return null;
  const belege = ps.reduce((s, p) => s + (Array.isArray(p?.evidence) ? p.evidence.length : 0), 0);
  const kr = kernRandZahl(data);
  const rollen = [...new Set(ps.filter((p) => p?.rolle === 'rand').map(kurzeRolle).filter(Boolean))].slice(0, 6);
  return { personas: ps.length, belege, kern: kr ? kr.kern : null, erweitert: kr ? { n: kr.rand, rollen } : null };
}

/** „Engineering, Einkauf und IT“ */
export function aufzaehlung(xs: string[]): string {
  return xs.length > 1 ? `${xs.slice(0, -1).join(', ')} ${t('und', 'and')} ${xs.at(-1)}` : (xs[0] ?? '');
}

// ─── Schritt 3: die Fassungen ────────────────────────────────────────────────

export type Von = 'kunde' | 'live' | 'uns';
/** Getter: die Sprache gilt beim Zugriff, nicht beim Laden des Moduls. */
export const VON_ETIKETT: Record<Von, string> = {
  get kunde() { return t('euer Entwurf', 'your draft'); },
  get live() { return t('heute live', 'live today'); },
  get uns() { return t('von uns', 'by us'); },
};

export interface FassungsKarte {
  nr: number;
  /** Sprungziel des Akkordeons mit dem Wortlaut. */
  anker: string;
  name: string;
  von: Von | null;
  ansatz: string;
  laenge: string;
  stufe: FassungsStufe | null;
  /** Bei Runs ohne Vergleich: wie viele weitergehen würden. */
  weiter: string;
  /** „Überzeugt vor allem die erweiterte Zielgruppe …“, aus den Zahlen (kern-rand.ts). */
  warnung: string | null;
  /** Die beste Fassung (höchste Stufe, bei Gleichstand die spätere), mit Rand hervorgehoben. */
  beste: boolean;
}

/**
 * Was der Autor zu einer Fassung geschrieben hat (`narrative.fassungen`): über
 * `run_id`, sonst, wenn die Einträge keine tragen, über die Reihenfolge.
 */
export function autorZurFassung(data: any, v: any, i: number): { von: Von | null; name: string; ansatz: string } | null {
  const liste: any[] = Array.isArray(data?.narrative?.fassungen) ? data.narrative.fassungen : [];
  if (!liste.length) return null;
  const mitRun = liste.some((f) => text(f?.run_id));
  const f = mitRun ? liste.find((x) => text(x?.run_id) && text(x.run_id) === text(v?.run_id)) : liste[i];
  if (!f) return null;
  const von = (['kunde', 'live', 'uns'] as const).find((x) => x === f.von) ?? null;
  return { von, name: text(f.name), ansatz: text(f.ansatz) };
}

export function fassungsKarten(data: any): FassungsKarte[] {
  const vs = varianten(data);
  const karten = vs.map((v, i): FassungsKarte => {
    const autor = autorZurFassung(data, v, i);
    const kr = v?.mode === 'feedback' ? null : leseKernRand(v);
    const urteile = anzahlUrteile(v) > personaNoten(v).length;
    return {
      nr: i + 1,
      anker: `fassung-${i + 1}`,
      name: autor?.name || fassungsname(v?.label) || t(`Fassung ${i + 1}`, `Version ${i + 1}`),
      von: autor?.von ?? null,
      ansatz: autor?.ansatz ?? '',
      laenge: rundZeichen(v?.text),
      stufe: textStufe(v),
      weiter: weiterSatz(v),
      warnung: kr ? (randWarnung(kr, { vergleich: vergleichFuerSatz(data, v), urteile }) ?? kr.warnung) : null,
      beste: false,
    };
  });
  if (karten.length) {
    let best = karten.length - 1;
    karten.forEach((k, i) => { if (k.stufe && (!karten[best]!.stufe || k.stufe.stufe >= karten[best]!.stufe!.stufe)) best = i; });
    karten[best]!.beste = true;
  }
  return karten;
}

/** Einleitung zu Schritt 3: die Frage des Autors, sonst wogegen jede Fassung antrat. */
export function fassungenEinleitung(data: any): { frage: string; satz: string } {
  const frage = text(data?.narrative?.frage);
  const vergleiche = varianten(data).filter((v) => v?.mode !== 'feedback').length;
  const satz = en()
    ? vergleiche
      ? vergleiche === 1
        ? `The version went up against ${vergleichImSatz(data)}. Each persona reads both texts without knowing which one is new and says which one they prefer.`
        : `Each version ran in its own comparison against ${vergleichImSatz(data)}. Each persona reads two texts without knowing which one is new and says which one they prefer.`
      : 'Each persona reads the version and says whether they would continue.'
    : vergleiche
      ? vergleiche === 1
        ? `Die Fassung trat gegen ${vergleichImSatz(data)} an. Jede Persona liest beide Texte, ohne zu wissen, welcher neu ist, und sagt, welchen sie vorzieht.`
        : `Jede Fassung lief in einem eigenen Vergleich gegen ${vergleichImSatz(data)}. Jede Persona liest dabei zwei Texte, ohne zu wissen, welcher neu ist, und sagt, welchen sie vorzieht.`
      : 'Jede Persona liest die Fassung und sagt, ob sie weitergehen würde.';
  return { frage, satz };
}

/**
 * Die zwei Prüfungen, nacheinander (seit 13.10.2026, Leser-Feedback zum
 * Magnolia-Bericht: „Sehen die Personas die Seiten mobile und am Desktop?“ und
 * „Ist dann doch das Design mit beurteilt worden?“). Steht oben in Schritt 3,
 * als zwei Kästen, wenn danach ein Gestaltungs-Schritt kommt; sonst nur der
 * Kasten zum Text. `gestaltungNr` ist die Nummer des Gestaltungs-Schritts.
 */
export interface Pruefung { titel: string; satz: string }

export function pruefungen(gestaltung: DesignSchritt | null, gestaltungNr: number | null): Pruefung[] {
  const text: Pruefung = {
    titel: t('Text-Simulation', 'Text simulation'),
    satz: t('Die Personas lesen den Text mit seiner Gliederung, also was Überschrift, Absatz oder Kleingedrucktes ist. Ein Bild der Seite sehen sie dabei nicht.',
      'The personas read the text with its structure, that is, what is a heading, a paragraph or small print. They do not see an image of the page.'),
  };
  if (!gestaltung) return [text];
  const wo = gestaltungNr ? t(` (Schritt ${gestaltungNr})`, ` (step ${gestaltungNr})`) : '';
  const satz = gestaltung.art === 'paar'
    ? t(`Die beste Fassung aus der Text-Simulation, in die Seite gesetzt. Die Personas scrollen sie als Bild durch, am Desktop und am Handy${wo}.`,
      `The best version from the text simulation, placed into the page. The personas scroll through it as an image, on desktop and on mobile${wo}.`)
    : t(`Die Personas scrollen die Seite als Bild durch, am Desktop und am Handy${wo}.`,
      `The personas scroll through the page as an image, on desktop and on mobile${wo}.`);
  return [text, { titel: t('Design-Simulation', 'Design simulation'), satz }];
}

/**
 * Schritt „Text-Simulation: das Ergebnis“ (seit 13.10.2026, vorher stand die Wortstufe in
 * den Karten von Schritt 3, und ein Leser verstand nicht, warum eine Karte
 * gerahmt war): die Fassungen nach Stufe, die beste zuerst, bei Gleichstand
 * die spätere. Ohne Stufe (Feedback-Runs) in ihrer Reihenfolge danach.
 */
export function ergebnisReihe(karten: FassungsKarte[]): FassungsKarte[] {
  return [...karten].sort((a, b) => {
    if (a.stufe && b.stufe) return b.stufe.stufe - a.stufe.stufe || b.nr - a.nr;
    if (a.stufe) return -1;
    if (b.stufe) return 1;
    return a.nr - b.nr;
  });
}

/**
 * „Text neben … lesen“. Den Namen des Autors nur, wenn er im Dativ gleich
 * bleibt, also mit einem Namen im Genitiv beginnt („Noras Entwurf“). „die
 * heutige Seite“ hieße dort „der heutigen Seite“; das bauen wir nicht nach.
 */
export function leseLink(data: any, v: any): string {
  if (!v || v.mode === 'feedback' || !text(v.baseline_text)) return t('Text lesen', 'Read text');
  if (vergleichIstHeute(data, v)) return t('Text neben der heutigen Seite lesen', 'Read text next to the current page');
  const vf = text(data?.narrative?.vergleichsfassung);
  if (/^[A-ZÄÖÜ][\p{L}-]*(s|')\s/u.test(vf)) return t(`Text neben ${vf} lesen`, `Read text next to ${vf}`);
  return t('Text neben der Vergleichsfassung lesen', 'Read text next to the baseline');
}

// ─── Schritt 4: die Gestaltung ───────────────────────────────────────────────

export interface Bilder { desktop: string | null; mobil: string | null }

export type DesignSchritt =
  | { art: 'paar'; titel: string; name: string; stufen: GeraetStufe[]; bilder: { heute: Bilder | null; neu: Bilder | null } }
  | { art: 'einzeln'; titel: string; note: number; was: string; satz: string };

/**
 * Schritt 4, nur mit Design-Checks. Ein Paar aus heutiger Seite und Skizze:
 * „Design-Simulation: „<Fassung>“ als Seite“ mit Wortstufe je Gerät. Nur ein
 * Check: „Die Seite als Bild geprüft“ mit der Note des ersten Eindrucks und
 * einem Satz, ohne Wortstufe (es gibt nichts, wogegen sie stünde).
 */
export function designSchritt(data: any, refs: Record<string, FassungRef> = {}): DesignSchritt | null {
  const eintraege = designEintraege(Array.isArray(data?.design) ? data.design : []);
  const paar = eintraege.find((e) => e.art === 'paar');
  if (paar && paar.art === 'paar') {
    const stufen = designStufen(paar.heute, paar.skizze);
    if (stufen.length) {
      const ref = refs[paar.skizze?.skizze?.fassung_hash ?? ''];
      const v = ref ? varianten(data)[ref.nr - 1] : null;
      const name = v ? (autorZurFassung(data, v, ref!.nr - 1)?.name || fassungsname(v.label)) : '';
      return {
        art: 'paar',
        titel: name
          ? t(`Design-Simulation: „${name}“ als Seite`, `Design simulation: “${name}” as a page`)
          : t('Design-Simulation: die neue Fassung als Seite', 'Design simulation: the new version as a page'),
        name, stufen,
        bilder: { heute: bilderVon(paar.heute?.bild_links), neu: bilderVon(paar.skizze?.bild_links) },
      };
    }
  }
  const k = designKarte(data?.design);
  if (!k) return null;
  return { art: 'einzeln', titel: t('Design-Simulation: die Seite als Bild', 'Design simulation: the page as an image'), note: k.note, was: k.was, satz: k.satz };
}

// ─── Der erste Bildschirm als eigener Schritt ────────────────────────────────

export interface ErsteVarianteZeile {
  nummer: number;
  name: string;
  /** Anteil der Personas, die hier zuerst klicken, 0 bis 1. */
  anteil: number;
  zuerst: number;
  /** „Kern 5 von 8“, wenn es Kern und Rand gibt. */
  kern: string;
  /** „6 von 8 bleiben, 2 springen ab“ (seit erster@2), sonst leer. */
  bleiben: string;
  bild: string | null;
  vorn: boolean;
}

export interface ErsterBildschirmSchritt {
  titel: string;
  /** Das Urteil des Tests in einem Satz, sonst leer. */
  urteil: string;
  personas: number;
  herkunft: string;
  /** Die Varianten, die zuerst geklickte vorn. */
  varianten: ErsteVarianteZeile[];
  /** Sprungziel des Akkordeons mit Bildern und allen Zahlen. */
  anker: string;
}

/**
 * Der Vergleich der ersten Bildschirme (test_first_screen) als eigener Schritt
 * der Zeitleiste, direkt nach der Zielgruppe (seit 13.10.2026, Tobias: im
 * sichtbaren Bereich entscheidet sich, ob jemand bleibt oder abspringt). Bis
 * dahin stand er nur zugeklappt unten im Akkordeon. Gezeigt wird der erste Test
 * des Berichts; das Akkordeon bleibt für Bilder und alle Zahlen. Ohne Test null.
 */
export function ersterBildschirmSchritt(data: any): ErsterBildschirmSchritt | null {
  const tests: any[] = Array.isArray(data?.erster_bildschirm) ? data.erster_bildschirm : [];
  const i = tests.findIndex((x) => Array.isArray(x?.varianten) && x.varianten.length >= 2);
  if (i < 0) return null;
  const test = tests[i];
  const personas = Number(test.personas) || 0;
  const fuehrt = test?.urteil?.fuehrt ?? null;
  const varianten = (test.varianten as any[]).map((v): ErsteVarianteZeile => ({
    nummer: Number(v?.nummer) || 0,
    name: text(v?.name) || t(`Variante ${v?.nummer ?? ''}`, `Variant ${v?.nummer ?? ''}`).trim(),
    anteil: Math.max(0, Math.min(1, Number(v?.anteil) || 0)),
    zuerst: Number(v?.zuerst_geklickt) || 0,
    kern: v?.kern ? t(`Kern ${v.kern.zuerst_geklickt} von ${v.kern.von}`, `core ${v.kern.zuerst_geklickt} of ${v.kern.von}`) : '',
    bleiben: bleibenText(v ?? {}),
    bild: text(v?.bild_links?.mobil) || text(v?.bild_links?.desktop) || null,
    vorn: fuehrt != null && v?.nummer === fuehrt,
  })).sort((a, b) => b.anteil - a.anteil || a.nummer - b.nummer);
  return {
    titel: t('Above the Fold: welche Variante zuerst geklickt wird', 'Above the fold: which variant gets clicked first'),
    urteil: text(test?.urteil?.satz),
    personas,
    herkunft: text(test?.herkunft),
    varianten,
    anker: `akkordeon-erster-bildschirm-${i + 1}`,
  };
}

// ─── Der offene letzte Schritt: Nächste Runde ────────────────────────────────

export interface OffeneFrage {
  frage: string;
  wie: string;
  /** Was der Kunde liefern müsste. Leer, wenn der Autor nichts nennt. */
  braucht: string;
}

export interface NaechsteRunde {
  /** „Drei Fragen hat dieser Test offen gelassen. So würden wir sie prüfen.“ */
  einleitung: string;
  fragen: OffeneFrage[];
}

/** Die Einleitung mit der Zahl als Wort. Subjekt ist „dieser Test“, das Verb bleibt deshalb „hat“. */
export function naechsteRundeSatz(n: number): string {
  if (en()) {
    return n === 1
      ? 'This test left one question open. This is how we would check it.'
      : `This test left ${zahlWort(n)} questions open. This is how we would check them.`;
  }
  const fragen = n === 1 ? 'Eine Frage' : `${gross(zahlWort(n))} Fragen`;
  return `${fragen} hat dieser Test offen gelassen. So würden wir sie prüfen.`;
}

/**
 * Der offene Schritt „Nächste Runde“ (seit 12.10.2026, Variante A im Canvas):
 * Fragen, die dieser Test offen ließ, je mit dem Test, der sie beantworten
 * würde, und was der Kunde dafür liefern müsste (`narrative.naechste_runde`).
 * Höchstens drei, ohne Frage fällt ein Eintrag weg. Ohne Feld null, und die
 * Zeitleiste endet wie bisher.
 */
export function naechsteRunde(data: any): NaechsteRunde | null {
  const roh: any[] = Array.isArray(data?.narrative?.naechste_runde) ? data.narrative.naechste_runde : [];
  const fragen = roh
    .map((r): OffeneFrage => ({ frage: text(r?.frage), wie: text(r?.wie), braucht: text(r?.braucht) }))
    .filter((r) => r.frage)
    .slice(0, 3);
  return fragen.length ? { einleitung: naechsteRundeSatz(fragen.length), fragen } : null;
}

// ─── Die Folge der Schritte ──────────────────────────────────────────────────

export type SchrittArt = 'lage' | 'zielgruppe' | 'erster_bildschirm' | 'fassungen' | 'ergebnis' | 'gestaltung' | 'naechste_runde';

export interface SchrittPlan {
  art: SchrittArt;
  nr: number;
  /** Der letzte Schritt hat keine Linie nach unten. */
  letzter: boolean;
  /** Noch nicht getestet: gestrichelter Kreis, nur „Nächste Runde“. */
  offen: boolean;
}

/**
 * Welche Schritte die Zeitleiste zeigt, in fester Reihenfolge, durchgezählt
 * ab 1. Was fehlt, rückt nicht als Lücke nach, die Nummern bleiben lückenlos.
 * Die Nächste Runde ist immer der letzte Schritt; der bisher letzte bekommt
 * dann seine Linie zu ihr.
 */
export function schrittFolge(da: Record<SchrittArt, boolean>): SchrittPlan[] {
  const arten = (['lage', 'zielgruppe', 'erster_bildschirm', 'fassungen', 'ergebnis', 'gestaltung', 'naechste_runde'] as const).filter((a) => da[a]);
  return arten.map((art, i) => ({ art, nr: i + 1, letzter: i === arten.length - 1, offen: art === 'naechste_runde' }));
}
